/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {Subscription, switchMap, timer} from 'rxjs';

import template from './line-item-diagnostics-page.component.html';
import {StatsApiService} from '../common/controller/stats-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

import {interpolateTemplate} from '../common/filters/interpolate-template';
import {DiagnosticRequestApiService} from './diagnostic-request-api.service';
import {BidUpdatesApiService} from './bid-updates-api.service';

@Component({
  standalone: false,
  selector: 'adn-line-item-diagnostics-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemDiagnosticsPageComponent implements OnInit, OnDestroy {
  @Input() model: any;

  readonly launchDisabled = signal(false);
  readonly showBidUpdates = signal(false);
  isAxAdvertiser = false;
  infoReset: any = {};
  readonly results = signal<any>({messages: []});
  readonly hasBidResults = signal(false);
  readonly bidProcessing = signal(false);
  readonly siteBids = signal<any[]>([]);
  sites: any[] = [];

  private readonly intervalRepeat = 40;
  private request: any;
  private intervalCounter = 0;
  private statsSubscription?: Subscription;
  private pollSubscription?: Subscription;

  constructor(
    private readonly statsApi: StatsApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly bidUpdatesApi: BidUpdatesApiService,
    private readonly diagnosticRequestApi: DiagnosticRequestApiService
  ) {
  }

  ngOnInit(): void {
    this.showBidUpdates.set(this.model?.bidSpecification?.bidStrategy === 'DYNAMIC');
    this.isAxAdvertiser = this.LocalUserPermissions.isAxAdvertiser();
    this.initData();
  }

  ngOnDestroy(): void {
    this.statsSubscription?.unsubscribe();
    this.stopPolling();
  }

  getFloorPrices(): void {
    if (this.infoReset.info) {
      this.infoReset.info();
    }
  }

  getBidUpdates(): void {
    this.bidProcessing.set(true);
    this.hasBidResults.set(false);
    const statsStartDate = moment.utc().subtract(1, 'd');
    this.statsSubscription?.unsubscribe();
    this.statsSubscription = this.statsApi.get({
      lineItemId: this.model.id,
      startDate: statsStartDate,
      groupBy: 'SITE'
    }).pipe(switchMap((response) => {
        this.sites = _.sortBy(response.chunks, ['totalCost.amount']).reverse().map((chunk) => {
          const site = chunk.site;
          site.cost = chunk.totalCost;
          return site;
        });
        return this.bidUpdatesApi.getForLineItem(this.model.id);
      })).subscribe({
        next: (data) => {
          const siteBids = [];
          if (_.isArray(data)) {
            const bidsBySite: Record<string, any> = {};
            data.forEach((siteUpdates) => {
              siteUpdates.updates = siteUpdates.updates.filter((bid) => moment(bid.time) > statsStartDate);
              if (siteUpdates.site && siteUpdates.site.id) {
                bidsBySite[siteUpdates.site.id] = siteUpdates;
              }
            });
            this.sites.forEach((site) => {
              const matchingBids = bidsBySite[site.id];
              if (matchingBids) {
                matchingBids.cost = site.cost;
                siteBids.push(matchingBids);
              }
            });
          }
          this.siteBids.set(siteBids);
          this.hasBidResults.set(true);
          this.bidProcessing.set(false);
        },
        error: () => {
          this.bidProcessing.set(false);
        }
      });
  }

  launchRequest(): void {
    this.initData();
    this.launchDisabled.set(true);
    this.diagnosticRequestApi.start({
      testType: 'LINE_ITEM',
      lineItem: this.model.id
    }).subscribe({
      next: (result) => {
        this.request = result;
        this.startPolling();
      },
      error: () => {
        this.failDiagnostics();
      }
    });
  }

  private initData(): void {
    this.stopPolling();
    this.results.set({messages: []});
    this.intervalCounter = 0;
  }

  private checkComplete(): void {
    this.diagnosticRequestApi.get(this.request.id).subscribe({
      next: (results) => this.applyDiagnosticResults(results),
      error: () => this.failDiagnostics()
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollSubscription = timer(1250, 5000).pipe(
      switchMap(() => this.diagnosticRequestApi.get(this.request.id))
    ).subscribe({
      next: (results) => this.applyDiagnosticResults(results),
      error: () => this.failDiagnostics()
    });
  }

  private applyDiagnosticResults(results: any): void {
    results.results.forEach((result) => {
      result.message.apiTranslation = interpolateTemplate(result.message.text)(result.message.parameters);
    });
    this.intervalCounter++;
    if (this.diagnosticRequestApi.isCompleted(results)) {
      this.results.set(results);
      this.stopPolling();
      this.launchDisabled.set(false);
    } else if (!this.diagnosticRequestApi.inProgress(results) || this.intervalCounter >= this.intervalRepeat) {
      this.results.set({...results, failed: true});
      this.stopPolling();
      this.launchDisabled.set(false);
    } else {
      this.results.set(results);
    }
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private failDiagnostics(): void {
    this.stopPolling();
    this.results.update((results) => ({...results, failed: true}));
    this.launchDisabled.set(false);
  }
}
